<template>
	<div class="costos_tab report_theme_container" data-theme="costos">
		<TabHeader
			title="Costos"
			description="Analiza la tendencia y composición de tus costos"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth">
			<template #center-sub-nav>
				<div class="filters_row is-flex is-align-items-center">
					<div class="sub_nav_filters">
						<button type="button"
							class="filter_btn"
							:class="{ 'is-active': isCuentasMode }"
							@click.stop="setCuentasMode">
							Cuentas
						</button>
					</div>

					<MultiSelectDropdown
						:value="selectedCounterpartTypes"
						:options="counterpartTypeOptions"
						placeholder="Tipos de contraparte"
						@input="SET_SELECTED_COUNTERPART_TYPES" />
				</div>
			</template>
		</TabHeader>

		<!-- ================================================================ -->
		<!-- Summary Cards Section                                             -->
		<!-- Reusa CardVentas. El slot #extra muestra el badge "Margen Bruto", -->
		<!-- la conclusión inline que distingue Costos del resto de tabs.     -->
		<!-- Colapsable: storageKey global (financialReports:*) sincroniza el  -->
		<!-- estado entre todos los tabs de finanzas.                         -->
		<!-- ================================================================ -->
		<CollapsibleSection class="mt-1"
			title="Resumen"
			storageKey="financialReports:summary-expanded">
			<template v-if="summaryHint" #hint>{{ summaryHint }}</template>
			<div class="cards_container is-flex">
				<CardVentas v-for="(card, index) in displayCards" :key="index" v-bind="card" class="mr-4">
					<template #extra>
						<div v-if="card.grossMargin" class="margin_extra is-flex is-align-items-center">
							<ChartLine class="margin_icon mr-1" />
							<span class="margin_label">Margen Bruto</span>
							<span class="margin_badge ml-auto">{{ card.grossMargin }}</span>
						</div>
					</template>
				</CardVentas>
			</div>
		</CollapsibleSection>

		<!-- Main Table Section -->
		<ExpensesEvolutionTable class="mt-2"
			title="Evolución de Costos"
			emptyMessage="No hay datos de costos para el período seleccionado."
			:monthHeaders="monthHeaders"
			:visibleRows="visibleRows"
			:totalRow="totalRow"
			:ratioRow="ratioRow"
			:level2RatioRows="level2RatioRows"
			:expandedL2Rows="expandedL2Rows"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			:filterPeriod="filterPeriod"
			:showAccumulated="true"
			:clickableCells="true"
			@toggle-l2="toggleL2"
			@change-period="setPeriodRange"
			@cell-click="openCostsAnalysis"
			@retry="fetchCostsData" />
	</div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import ChartLine from "@/components/svg/ChartLine.vue";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import CardVentas from "@/components/financialReports/CardVentas.vue";
import ExpensesEvolutionTable from "@/components/financialReports/ExpensesEvolutionTable.vue";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight.vue";
import HeaderCostsAnalysis from "@/components/financialReports/costsAnalysis/HeaderCostsAnalysis.vue";
import BodyCostsAnalysis from "@/components/financialReports/costsAnalysis/BodyCostsAnalysis.vue";
import {
	PERIOD_TYPES,
	COST_COUNTERPART_TYPES,
	COST_CARD_PROGRESS_COLORS,
	FILTER_PERIOD_OPTIONS,
} from "@/config/reportStructure";
import { formatPercent, parseMonthLabel } from "../utils/periodFormatting";
import { buildBaseCard, emptyCard } from "../utils/summaryCards";
import { parseToMoney } from "@/utils/currency";
import { capitalize } from "@/utils/string";

// grossMargin: "" → el v-if del slot oculta el badge mientras no haya datos.
// Mostrar "—" daría la falsa señal de un dato calculado en cero.
const EMPTY_CARD = () => emptyCard({ grossMargin: "" });

export default {
	name: "CostosTab",

	components: {
		TabHeader,
		CollapsibleSection,
		ChartLine,
		MultiSelectDropdown,
		CardVentas,
		ExpensesEvolutionTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			counterpartTypeOptions: COST_COUNTERPART_TYPES,
			expandedL2Rows: [],
			filterOptions: FILTER_PERIOD_OPTIONS,
			// ID del setTimeout del debounce de category-change del modal de
			// análisis. En data() (no `this._xxx`) para ser forward-compat con
			// Vue 3 — el Proxy intercepta toda asignación a `this`.
			categoryChangeTimer: null,
		};
	},

	computed: {
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("classification", ["categories", "itemsByCategory"]),
		...mapState("costsReport", [
			"selectedCounterpartTypes",
			"monthHeaders",
			"isLoading",
			"error",
			"summaryCards",
		]),
		// Lente activo del modal de análisis — el header puede cambiar el mes y
		// los filtros Tiendas/Marcas; retenemos account/selectedItems/month para
		// reusarlos en el refetch sin leer state.x directo desde un handler.
		...mapState("costsAnalysis", {
			analysisAccount: "account",
			analysisSelectedItems: "selectedItems",
			analysisMonth: "month",
		}),
		...mapGetters("costsReport", ["tableRows", "totalRow", "ratioRow", "level2RatioRows", "isCuentasMode"]),

		getNow() {
			return moment().toDate();
		},

		// Hint compacto que se muestra al lado del label "Resumen" cuando la
		// sección está colapsada — preserva el monto del mes actual para que
		// el modo foco no pierda contexto crítico.
		summaryHint() {
			const amount = this.summaryCards && this.summaryCards.current_month
				? this.summaryCards.current_month.amount
				: null;
			return amount != null ? parseToMoney(amount) : "";
		},

		visibleRows() {
			return this.tableRows.filter(row => {
				if (row.level === 2) return true;
				return this.expandedL2Rows.includes(row.parent);
			});
		},

		displayCards() {
			if (!this.summaryCards) {
				return [EMPTY_CARD(), EMPTY_CARD(), EMPTY_CARD()];
			}
			const { current_month, ytd, ltm } = this.summaryCards;
			return [
				this.buildCard(current_month, { showMom: true }),
				this.buildCard(ytd),
				this.buildCard(ltm),
			];
		},

		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.selectedCounterpartTypes.join(","),
				this.filterPeriod,
			].join("|");
		},

		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},

		// Categorías dinámicas (tiendas/marcas/…) para los SelectComponent del
		// header del modal. Options del classification store (itemsByCategory),
		// poblado por `classification/getCategories` (short-circuit si ya está).
		headerCategories() {
			return this.categories.map((cat) => ({
				id: cat,
				label: capitalize(cat),
				options: (this.itemsByCategory[cat] || []).map((name) => ({
					value: name,
					label: name,
				})),
			}));
		},
	},

	methods: {
		...mapActions("costsReport", ["fetchCostsData", "fetchCostsSummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapActions("costsAnalysis", ["fetchCostsAnalysis"]),
		// Categorías + items son globales (no específicos de Costos); el store
		// hace short-circuit si ya están cargados por otro view.
		...mapActions("classification", ["getCategories"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("costsReport", [
			"SET_SELECTED_COUNTERPART_TYPES",
		]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),
		...mapMutations("costsAnalysis", {
			resetCostsAnalysis: "RESET",
			setSelectedItems: "SET_SELECTED_ITEMS",
		}),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		setCuentasMode() {
			this.SET_SELECTED_COUNTERPART_TYPES([]);
		},
		toggleL2(label) {
			const index = this.expandedL2Rows.indexOf(label);
			if (index > -1) {
				this.expandedL2Rows.splice(index, 1);
			} else {
				this.expandedL2Rows.push(label);
			}
		},

		buildCard(card, options = {}) {
			return {
				...buildBaseCard(card, COST_CARD_PROGRESS_COLORS, options),
				grossMargin: card.gross_margin != null ? formatPercent(card.gross_margin) : "",
			};
		},

		// --- Modal de Análisis de Costos -------------------------------------

		// Cell-click de la tabla → abre el drawer de análisis del mes. El estado
		// de costos es siempre del mes completo (como el headline); `account`
		// sólo recorta el ranking de contrapartes y sólo aplica a una cuenta L3
		// real (una fila L2 no es un level_3, va null).
		openCostsAnalysis({ monthLabel, account, level }) {
			const month = parseMonthLabel(monthLabel);
			if (!month) return;

			const drillAccount = level === 3 ? account : null;
			const monthDate = moment(month, "YYYY-MM").toDate();
			const periodLabel = moment(month, "YYYY-MM")
				.locale("es")
				.format("MMM YYYY")
				.replace(/^./, (c) => c.toUpperCase());

			this.fetchCostsAnalysis({ month, account: drillAccount, groupBy: null, selectedItems: {} });
			this.$buefy.modal.open({
				parent: this,
				hasModalCard: true,
				customClass: "wide-summary-modal",
				canCancel: ["escape", "outside"],
				component: ModalFullHeightRight,
				props: {
					headerComp: HeaderCostsAnalysis,
					bodyComp: BodyCostsAnalysis,
					headerProps: {
						periodLabel,
						endMonth: monthDate,
						getNow: this.getNow,
						categories: this.headerCategories,
					},
					bodyProps: null,
					// El modal se monta en document.body — sin esto el subárbol
					// no hereda el [data-theme="costos"] del .costos_tab.
					theme: "costos",
				},
				events: {
					closed: this.onAnalysisClose,
					"update-end-month": this.onAnalysisMonthChange,
					"category-change": this.onCategoryChange,
				},
			});
		},

		// El usuario cambió el mes desde el AnualCalendar del header del drawer.
		// account/selectedItems se conservan — sólo cambia el período.
		onAnalysisMonthChange(date) {
			const month = moment(date).format("YYYY-MM");
			this.fetchCostsAnalysis({
				month,
				account: this.analysisAccount,
				groupBy: null,
				selectedItems: this.analysisSelectedItems,
			});
		},

		// Cambio de filtro Tiendas/Marcas en el header del drawer. Merge inmediato
		// al store (el header refleja el estado sin esperar al fetch) + refetch
		// debounced (varios toggles seguidos colapsan en un solo request).
		onCategoryChange({ id, values }) {
			const merged = { ...this.analysisSelectedItems, [id]: values };
			this.setSelectedItems(merged);
			clearTimeout(this.categoryChangeTimer);
			this.categoryChangeTimer = setTimeout(() => {
				this.fetchCostsAnalysis({
					month: this.analysisMonth,
					account: this.analysisAccount,
					groupBy: null,
					selectedItems: merged,
				});
			}, 600);
		},

		// Cierre del drawer: cancela el fetch debounced pendiente antes de
		// resetear para no dispatchear contra un store ya limpio.
		onAnalysisClose() {
			clearTimeout(this.categoryChangeTimer);
			this.categoryChangeTimer = null;
			this.resetCostsAnalysis();
		},
	},

	mounted() {
		this.fetchCostsData();
		this.fetchCostsSummary();
		// Alimenta `categories` + `itemsByCategory` que consume headerCategories
		// (dropdowns Tiendas/Marcas del modal). Short-circuit si ya cargó.
		this.getCategories();
	},

	beforeDestroy() {
		// Si el tab se desmonta con un fetch debounced pendiente, lo cancelamos
		// para evitar dispatchear contra un store/component fuera de scope.
		clearTimeout(this.categoryChangeTimer);
	},

	watch: {
		fetchSignature() { this.fetchCostsData(); },
		summarySignature() { this.fetchCostsSummary(); },
	},
};
</script>

<style scoped lang="scss">
.costos_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

// Header común vive en TabHeader.vue. Replicamos las classes del slot
// #center-sub-nav (botón Cuentas + MultiSelectDropdown) localmente porque
// TabHeader es scoped y sus estilos no llegan al markup inyectado al slot.

.filters_row {
	gap: 12px;
}

.sub_nav_filters {
	display: inline-flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
	box-sizing: border-box;
	min-height: 34px;
}

.filter_btn {
	display: inline-flex;
	align-items: center;
	padding: 0 16px;
	min-height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.15s ease;
	box-sizing: border-box;

	&:hover { background: var(--report-surface); }

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}

.cards_container {
	width: 100%;
}

// "Margen Bruto X%" — signature de Costos. Icon+label a la izquierda sobre la
// card (sin fondo), y el porcentaje en una pill aparte empujada a la derecha
// con ml-auto. Mismo patrón visual que Eficiencia en Gastos.
.margin_extra {
	width: 100%;
	font-size: 13px;
}

.margin_icon {
	width: 14px;
	height: 14px;
	color: var(--report-text-muted-soft);
}

.margin_label {
	color: var(--report-text-muted);
	font-weight: 500;
}

.margin_badge {
	display: inline-flex;
	align-items: center;
	padding: 4px 10px;
	border-radius: 999px;
	background: var(--report-accent-bg);
	color: var(--report-accent-text);
	border: 1px solid var(--report-accent-border);
	font-size: 12px;
	font-weight: 700;
}
</style>
